import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Paper, Stepper, Step, StepLabel, Button, Typography,
  Select, MenuItem, FormControl, InputLabel,
  Autocomplete, TextField, Alert, Stack,
} from '@mui/material';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import { AWS_REGIONS } from '../../../constants/awsRegions';
import {
  enableDetective, getDetectiveConfig, resolveDetectiveMemberEmails,
} from '../../../services/detectiveApi';
import { listSecurityHubAccounts } from '../../../services/securityhubApi';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { POLL_INTERVAL_MS } from './pollInterval';
import MultiSelectField from './MultiSelectField';
import OperationProgress from './OperationProgress';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  CK, FONT_BODY, sectionCardSx, sectionHeadingSx, actionRowSx,
} from '../portalStyles';

// Must match `color` in engines/detective.js.
const DETECTIVE_COLOR = '#3949AB';
const DETECTIVE_COLOR_DARK = '#283593';

const steps = ['Administrator account', 'Members and regions', 'Root emails'];
const EMAILS_STEP = 2;

// Mirrors detectiveMemberEmailMaxLen on the backend. Validated CLIENT-SIDE as
// well as server-side because the server-side failure is a 400 that kills the
// WHOLE CreateMembers batch: one over-long address stops up to 49 other accounts
// in the same call from enrolling. Catching it in the field turns a region-wide
// enrollment failure into a red outline on one input.
const EMAIL_MAX = 64;

// The chunk size resolveDetectiveMemberEmails uses, needed only to phrase the
// "N of M lookups failed" banner in the same units the errors arrive in.
const RESOLVE_CHUNK_SIZE = 5;

// Detective-accented primary button (the theme default is the CSPM blue).
const detectiveButtonSx = {
  backgroundColor: DETECTIVE_COLOR,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { backgroundColor: DETECTIVE_COLOR_DARK },
  '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
};

const quietButtonSx = {
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  color: CK.body,
  '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.26)' },
};

const bodyTextSx = { fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body };

// emailProblem returns the reason a typed address cannot be submitted, or '' when
// it is acceptable. It is deliberately WEAKER than the backend's regex — it
// requires only an "@" — because a stricter client rule would reject an address
// AWS holds and the server would have accepted, and the field is the only way in.
// The backend remains the boundary; this exists so the operator learns about the
// 64-character cap here rather than through a failed batch.
const emailProblem = (raw) => {
  const value = (raw || '').trim();
  if (!value) return 'An AWS root user email address is required.';
  if (value.length > EMAIL_MAX) {
    return `This address is ${value.length} characters; Amazon Detective accepts at most ${EMAIL_MAX}, `
      + 'and a longer one rejects the whole enrollment batch.';
  }
  if (!value.includes('@')) return 'Enter a valid email address.';
  return '';
};

// One row of the review block — a fixed label column plus the chosen value, so
// every row shares a single baseline grid.
function ReviewRow({ label, value }) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: 0.25, sm: 2 }}
      sx={{ py: 0.5 }}
    >
      <Typography sx={{ minWidth: 160, fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.muted }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: FONT_BODY,
          fontSize: '0.875rem',
          fontWeight: 600,
          color: CK.navy,
          wordBreak: 'break-word',
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

// DetectiveSetup is the three-step enable wizard.
//
// `availability` is a PROP, not a fetch: DetectiveEntry already read
// GET /detective/availability on mount, and its `supportedRegions` array is the
// wizard's only source for the region picker. Re-requesting it here would be a
// second round-trip for a payload the parent is holding.
export default function DetectiveSetup({ availability, onComplete, priorFailure }) {
  const [active, setActive] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Form state. homeRegion starts EMPTY rather than at a default region: a
  // pre-filled default would be a region literal in this file, which is exactly
  // the second source of truth the picker is filtered from the backend to avoid.
  const [adminAccountId, setAdminAccountId] = useState('');
  const [homeRegion, setHomeRegion] = useState('');
  const [linkingRegions, setLinkingRegions] = useState([]);
  const [memberAccountIds, setMemberAccountIds] = useState([]);

  // Root-email resolution state.
  const [emailRows, setEmailRows] = useState([]); // [{accountId, email, resolved, reason}]
  const [manualEmails, setManualEmails] = useState({}); // accountId -> typed root email
  const [resolvingEmails, setResolvingEmails] = useState(false);
  const [resolveProgress, setResolveProgress] = useState(null); // {done, total}
  // Accounts the SERVER refused on a 422, which is not the same set the client
  // resolver failed on: an address can auto-resolve here and still be rejected
  // there (malformed, too long, or not the account's real root user).
  const [missing, setMissing] = useState([]);
  // The member selection the current emailRows were resolved for. Without it,
  // returning to this step from a 422 re-fires the whole resolve — up to 240
  // sequential requests at Detective's 1,200-member ceiling — to learn what the
  // wizard already knows.
  const resolvedForRef = useRef(null);

  // In-progress state (after submit).
  const [config, setConfig] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const acctRes = await listSecurityHubAccounts();
        setAccounts(acctRes?.data?.accounts || acctRes?.data || []);
      } catch (e) {
        setError(e.message);
      }
    })();
  }, []);

  // Poll while in_progress so the wizard hands back to DetectiveEntry once the
  // enable settles.
  useEffect(() => {
    if (!config || config.status !== 'in_progress') return undefined;
    const id = setInterval(async () => {
      try {
        const res = await getDetectiveConfig();
        const next = res?.data;
        if (next) setConfig(next);
        if (next && next.status !== 'in_progress') {
          clearInterval(id);
          if (onComplete) onComplete();
        }
      } catch (_) { /* swallow transient — the next tick retries */ }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [config, onComplete]);

  // The region picker's options: AWS_REGIONS intersected with the allowlist the
  // backend serves on GET /detective/availability.
  //
  // There is NO region list in this file. Detective is absent from two commercial
  // regions and a JS copy of that fact would drift silently — nothing in this repo
  // has a frontend test that would catch it, and the symptom is a region the
  // wizard offered and the enable refuses.
  //
  // Both fallbacks degrade to MORE choice, never less: an absent array (an older
  // backend) and an intersection that comes out empty (an array of codes this
  // shared list does not carry) both fall back to the unfiltered AWS_REGIONS and
  // let the backend refuse. An empty picker is indistinguishable from a broken
  // page, and the backend guard is the real boundary — this only stops the
  // operator reaching it.
  //
  // Regions the ACCOUNT has not opted into are deliberately not filtered here:
  // that is a per-account fact the wizard does not have, and the backend's
  // "<region> is not enabled for account <id>" refusal names the account, which a
  // region picker could not.
  const regionOptions = useMemo(() => {
    const supported = availability?.supportedRegions;
    if (!Array.isArray(supported) || supported.length === 0) return AWS_REGIONS;
    const allow = new Set(supported);
    const filtered = AWS_REGIONS.filter((r) => allow.has(r.value));
    return filtered.length > 0 ? filtered : AWS_REGIONS;
  }, [availability]);

  const next = () => setActive((s) => s + 1);
  const back = () => setActive((s) => s - 1);

  const memberKey = memberAccountIds.join(',');

  const resolveEmails = useCallback(async (ids) => {
    setResolvingEmails(true);
    setResolveProgress({ done: 0, total: ids.length });
    try {
      // resolveDetectiveMemberEmails RESOLVES even when chunks rejected — it
      // isolates failure per chunk and records the affected accounts as
      // unresolved. There is deliberately no catch that treats the call as
      // failed: doing so would throw away every address that DID resolve, and the
      // per-account fields below are already the recovery for the rest.
      const { data, errors } = await resolveDetectiveMemberEmails(ids, {
        onProgress: (done, total) => setResolveProgress({ done, total }),
      });
      const results = data?.results || [];
      setEmailRows(results);
      resolvedForRef.current = ids.join(',');
      // Drop manual entries for accounts no longer selected or now auto-resolved.
      setManualEmails((cur) => {
        const kept = {};
        results.forEach((row) => {
          if (!row.resolved && cur[row.accountId]) kept[row.accountId] = cur[row.accountId];
        });
        return kept;
      });
      // ONE banner above the fields, not one alert per failed chunk: at 240
      // chunks a per-chunk alert is its own denial of service. Each row already
      // carries its `reason` beside the field, which is where the operator acts.
      if (errors.length) {
        const chunks = Math.ceil(ids.length / RESOLVE_CHUNK_SIZE);
        setNotice(`${errors.length} of ${chunks} lookups failed. Enter those addresses below.`);
      } else {
        setNotice('');
      }
    } finally {
      setResolvingEmails(false);
      setResolveProgress(null);
    }
  }, []);

  // Resolve root emails on entering the step, and again whenever the member
  // selection changes — but not merely because the operator stepped back and
  // forth, and not on the return trip from a 422.
  useEffect(() => {
    if (active !== EMAILS_STEP) return;
    if (resolvedForRef.current === memberKey) return;
    if (memberAccountIds.length === 0) {
      setEmailRows([]);
      resolvedForRef.current = memberKey;
      return;
    }
    resolveEmails(memberAccountIds);
    // memberAccountIds is derived from memberKey; listing the array itself would
    // re-fire this on every render that rebuilds it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, memberKey, resolveEmails]);

  // accountId -> the server's reason, for the accounts a 422 named.
  const missingById = useMemo(() => {
    const map = {};
    missing.forEach((m) => { map[m.accountId] = m; });
    return map;
  }, [missing]);

  // A row needs typing when the client could not resolve it, OR when the server
  // refused the address it did resolve. The second case is why this is not just
  // `!row.resolved`: an auto-resolved address that AWS will not verify looks
  // perfectly fine here.
  const needsInput = useCallback(
    (row) => !row.resolved || !!missingById[row.accountId],
    [missingById],
  );

  const emailsReady = emailRows.length === memberAccountIds.length
    && emailRows.every((row) => !needsInput(row) || !emailProblem(manualEmails[row.accountId]));

  const canNext = () => {
    if (active === 0) return !!adminAccountId;
    if (active === 1) {
      return !!homeRegion && linkingRegions.length > 0 && linkingRegions.includes(homeRegion);
    }
    if (active === EMAILS_STEP) return !resolvingEmails && emailsReady;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      // Only the addresses the operator actually typed; auto-resolved accounts
      // are re-resolved server-side.
      const memberEmails = {};
      Object.entries(manualEmails).forEach(([acctId, val]) => {
        const v = (val || '').trim();
        if (v) memberEmails[acctId] = v;
      });
      const res = await enableDetective({
        adminAccountId,
        homeRegion,
        linkingRegions,
        memberAccountIds,
        memberEmails,
      });
      setMissing([]);
      setConfig(res?.data);
    } catch (e) {
      // A 422 carries `missingEmails` — the accounts whose root user email could
      // not be resolved or was refused. Send the operator back to THIS step with
      // exactly those flagged, rather than showing a generic failure about
      // accounts they cannot identify.
      if (e.missingEmails?.length) {
        setActive(EMAILS_STEP);
        setMissing(e.missingEmails);
        // e.message IS the server's sentence — the 422 body's `error` field, or
        // detectiveApi's byte-identical fallback. No third variant is written
        // here: a paraphrase that drops "root user" would make this banner
        // disagree with the field label two lines below it.
        setError(e.message);
        return;
      }
      setError(e.message || 'Could not enable Amazon Detective');
    } finally {
      setSubmitting(false);
    }
  };

  const accountLabel = (accountId) => {
    const acct = accounts.find((a) => a.accountId === accountId);
    return acct ? `${acct.accountName || acct.name || accountId} (${accountId})` : accountId;
  };

  const body = () => {
    if (config) {
      const statusLabel = {
        in_progress: 'Setting up Amazon Detective…',
        enabled: 'Amazon Detective is enabled',
        partial_failure: 'Amazon Detective enabled with partial failures',
        failed: 'Setup failed',
        disabled: 'Amazon Detective is disabled',
      }[config.status] || config.status;
      return (
        <Box sx={{ py: 2 }}>
          <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>{statusLabel}</Typography>
          {config.status === 'in_progress' && (
            <>
              <Typography sx={{ ...bodyTextSx, mt: 1 }}>
                Creating the behavior graph in each region, inviting every member
                account and waiting for AWS to verify each root user email address.
                This can take several minutes.
              </Typography>
              <OperationProgress
                percent={config.progress}
                label={config.progressLabel || 'Working…'}
                sx={{ my: 2 }}
              />
            </>
          )}
          {config.errorMessage && (
            <Alert severity="warning" sx={{ mt: 2 }}>{config.errorMessage}</Alert>
          )}
          <Box sx={{ mt: 2 }}>
            <ReviewRow label="Administrator account" value={config.adminAccountId} />
            <ReviewRow label="Home region" value={config.homeRegion} />
          </Box>
        </Box>
      );
    }

    if (active === 0) {
      return (
        <Box sx={{ py: 2 }}>
          <Typography sx={{ ...bodyTextSx, mb: 2 }}>
            Pick one linked account to own the Amazon Detective behavior graph. Every
            member account is invited into that account&rsquo;s graph, and all
            investigation data is read through it.
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Changing the administrator account later means disabling Amazon Detective
            and setting it up again — the behavior graph belongs to this account and
            cannot be transferred. It is not pre-filled from your GuardDuty
            administrator: the two are independent, and Detective can be administered
            from a different account entirely.
          </Alert>
          <Autocomplete
            options={accounts}
            getOptionLabel={(a) => `${a.accountName || a.name || a.accountId} (${a.accountId})`}
            value={accounts.find((a) => a.accountId === adminAccountId) || null}
            onChange={(_, v) => setAdminAccountId(v?.accountId || '')}
            renderInput={(params) => <TextField {...params} label="Administrator account" />}
          />
        </Box>
      );
    }

    if (active === 1) {
      const memberOptions = accounts.filter((a) => a.accountId !== adminAccountId);
      return (
        <Box sx={{ py: 2 }}>
          <Typography sx={{ ...bodyTextSx, mb: 2 }}>
            Pick the member accounts to enroll and the regions to cover. The
            administrator account is always in its own graph and does not need to be
            selected. Amazon Detective builds one behavior graph per region, so each
            region you add is separate coverage.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Home region</InputLabel>
            <Select
              value={homeRegion}
              label="Home region"
              onChange={(e) => {
                const v = e.target.value;
                setHomeRegion(v);
                if (!linkingRegions.includes(v)) setLinkingRegions([v, ...linkingRegions]);
              }}
            >
              {regionOptions.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
            </Select>
          </FormControl>
          <MultiSelectField
            label="Regions"
            options={regionOptions.map((r) => ({ value: r.value, label: r.label }))}
            value={linkingRegions}
            onChange={setLinkingRegions}
            lockedValues={homeRegion ? [homeRegion] : []}
            sx={{ mb: 2 }}
          />
          <MultiSelectField
            label="Member accounts"
            options={memberOptions.map((a) => ({
              value: a.accountId,
              label: `${a.accountName || a.name || a.accountId} (${a.accountId})`,
            }))}
            value={memberAccountIds}
            onChange={setMemberAccountIds}
          />
        </Box>
      );
    }

    if (active === EMAILS_STEP) {
      const total = resolveProgress?.total || 0;
      const done = resolveProgress?.done || 0;
      return (
        <Box sx={{ py: 2 }}>
          <Typography sx={{ ...bodyTextSx, mb: 1 }}>
            Amazon Detective enrolls member accounts by invitation, and AWS checks the
            address you supply against the account&rsquo;s real AWS root user. It must
            be that address and not the account owner&rsquo;s work address: a
            plausible-looking substitute is accepted here, passes validation, and then
            fails minutes later during enrollment with nothing on screen to explain
            why.
          </Typography>
          <Typography sx={{ ...bodyTextSx, mb: 2 }}>
            Prism looks each address up automatically where it can. Enter any it could
            not resolve.
          </Typography>
          {resolvingEmails && (
            <OperationProgress
              percent={total > 0 ? Math.round((done / total) * 100) : null}
              label={`Resolving root email addresses — ${done} of ${total} accounts`}
              sx={{ mb: 2 }}
            />
          )}
          {notice && <Alert severity="warning" sx={{ mb: 2 }}>{notice}</Alert>}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {!resolvingEmails && memberAccountIds.length === 0 && (
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.muted, mb: 2 }}>
              No member accounts selected — only the administrator account will be in
              the behavior graph. Go back to add members.
            </Typography>
          )}
          <Stack spacing={2}>
            {emailRows.map((row) => {
              const flagged = missingById[row.accountId];
              const typed = manualEmails[row.accountId] || '';
              const problem = emailProblem(typed);
              return (
                <Stack
                  key={row.accountId}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                >
                  {/* The account id is rendered beside every field, always. Without
                      it the operator is typing addresses into an unlabelled list of
                      identical boxes. */}
                  <Typography
                    sx={{
                      minWidth: { xs: 0, sm: 260 },
                      pt: { xs: 0, sm: 1 },
                      fontFamily: FONT_BODY,
                      fontSize: '0.875rem',
                      color: CK.navy,
                      wordBreak: 'break-word',
                    }}
                  >
                    {accountLabel(row.accountId)}
                  </Typography>
                  {needsInput(row) ? (
                    <TextField
                      size="small"
                      required
                      // "AWS root user email address", never "Email" — the same
                      // noun phrase the server's 422 body uses, so the wizard and
                      // the server can never disagree about what is being asked
                      // for.
                      label="AWS root user email address"
                      sx={{ flex: 1, minWidth: 0 }}
                      value={typed}
                      onChange={(e) => setManualEmails((m) => ({ ...m, [row.accountId]: e.target.value }))}
                      error={!!problem}
                      helperText={
                        problem
                        || flagged?.reason
                        || row.reason
                        || 'Enter this account’s AWS root user email address.'
                      }
                    />
                  ) : (
                    <Typography
                      sx={{
                        pt: { xs: 0, sm: 1 },
                        fontFamily: FONT_BODY,
                        fontSize: '0.875rem',
                        color: CK.green,
                        wordBreak: 'break-word',
                      }}
                    >
                      {row.email} (resolved automatically)
                    </Typography>
                  )}
                </Stack>
              );
            })}
          </Stack>
          <Box sx={{ mt: 3 }}>
            <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Review</Typography>
            <ReviewRow label="Administrator account" value={accountLabel(adminAccountId)} />
            <ReviewRow label="Home region" value={homeRegion} />
            <ReviewRow label="Regions" value={linkingRegions.join(', ')} />
            <ReviewRow
              label="Member accounts"
              value={`${memberAccountIds.length} (the administrator is in its own graph as well)`}
            />
          </Box>
        </Box>
      );
    }

    return null;
  };

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
        <PortalPageHeader
          icon={<TravelExploreOutlinedIcon sx={{ fontSize: 24 }} />}
          title="Set up Amazon Detective"
          subtitle="Enable behavior-graph investigation across your organization"
          color={DETECTIVE_COLOR}
        />
      </motion.div>
      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={sectionCardSx}>
          {priorFailure && !config && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              The previous setup attempt failed: {priorFailure}. Submitting again will
              clear that state and retry.
            </Alert>
          )}
          <Stepper
            activeStep={active}
            alternativeLabel
            sx={{
              mb: 3,
              overflowX: 'auto',
              pb: 1,
              '& .MuiStepLabel-label': { fontFamily: FONT_BODY, fontSize: '0.8rem', color: CK.body },
              '& .MuiStepLabel-label.Mui-active': { fontWeight: 600, color: CK.navy },
              '& .MuiStepLabel-label.Mui-completed': { fontWeight: 600, color: CK.navy },
              '& .MuiStepIcon-root.Mui-active': { color: DETECTIVE_COLOR },
              '& .MuiStepIcon-root.Mui-completed': { color: DETECTIVE_COLOR },
            }}
          >
            {steps.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
          </Stepper>
          {body()}
          {/* The error banner is rendered inside the emails step so it sits with
              the fields it is about; every other step shows it here. */}
          {error && active !== EMAILS_STEP && !config && (
            <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
          )}
          {!config && (
            <Box sx={{ ...actionRowSx, justifyContent: 'flex-end', mt: 3 }}>
              <Button disabled={active === 0} onClick={back} sx={quietButtonSx}>Back</Button>
              {active < steps.length - 1 ? (
                <Button variant="contained" disabled={!canNext()} onClick={next} sx={detectiveButtonSx}>
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  disabled={submitting || !canNext()}
                  onClick={submit}
                  sx={detectiveButtonSx}
                >
                  {submitting ? 'Enabling…' : 'Enable Amazon Detective'}
                </Button>
              )}
            </Box>
          )}
        </Paper>
      </motion.div>
    </motion.div>
  );
}
